import React, { useCallback, useMemo, useRef, useState } from 'react';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import * as formService from '../../form-services/customer-editor';
import { ICustomerPanelLaunchArgs } from '../../entities/customer-args';
import { useEventNotification } from '../../../../framework/services/event-notification';
import { ProjectEvents } from '../../events';
import { Customer, ICustomer } from '../../../../entities-generated/common/customer';
import { CustomerApiClient } from '../../../../services/customer';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { MessageBarType, PanelType } from '@fluentui/react';
import { BwjSidePanel } from '../../../../framework/components/bwj-side-panel';
import { BwjButtonType } from '../../../../framework/components/bwj-button';
import { CustomerEditor } from '../customer-editor';
import { CustomerViewer } from '../customer-viewer';

const subscriberId = 'Panel.Customer';

export const CustomerPanel: React.FunctionComponent = () => {
  const{ showToast } = useToast();
  const [showPanel, setShowPanel] = useState(false);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [inEditMode, setInEditMode] = useState(false);
  const [customer, setCustomer] = useState<ICustomer>(new Customer());
  const formModel = formService.useFormModel(subscriberId);
  const hasUnsavedChanges = formService.useHasUnsavedChanges(formModel, false);
  const projectId = useRef('');

  useEventNotification((args: ICustomerPanelLaunchArgs) => {
    projectId.current = args.projectId;
    setShowPanel(true);
    setViewState(BwjViewState.Loading);

    CustomerApiClient.getCustomer(projectId.current)
    .then(resp => {
      setCustomer(resp);
      formService.initializeService(resp);
      setViewState(BwjViewState.Ready);
    })
    .catch(e => {
      console.log(e);
      setViewState(BwjViewState.Error);
    });
  }, [], ProjectEvents.CustomerPanel_Launch, subscriberId);

  const onEditCustomer = useCallback(() => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid === false) { return undefined; }

      setViewState(BwjViewState.Loading);
      const request = formService.toUpdateRequest();
      return CustomerApiClient.updateCustomer(request, projectId.current);
    })
    .then(resp => {
      if(resp === undefined) { return; }

      setCustomer(formService.toCustomer());
      formService.setReversionState();
      formService.revertChanges();
      setInEditMode(false);
    })
    .catch(e => {
      console.log(e);
      showToast(MessageBarType.error, 'Error - customer update failed');
    })
    .finally(() => setViewState(BwjViewState.Ready));
  }, [showToast]);

  const onClosePanel = useCallback(() => setShowPanel(false), []);

  const footerButtons = useMemo(() => {
    if(inEditMode) {
      return [{
        buttonStyle: BwjButtonType.PrimaryStandard,
        text: 'Save changes',
        disabled: hasUnsavedChanges === false,
        onClick: onEditCustomer
      }, {
        buttonStyle: BwjButtonType.DefaultStandard,
        text: 'Cancel',
        onClick: () => setInEditMode(false)
      }];
    }

    return [{
      buttonStyle: BwjButtonType.PrimaryStandard,
      text: 'Edit customer',
      onClick: () => setInEditMode(true)
    }, {
      buttonStyle: BwjButtonType.DefaultStandard,
      text: 'Close',
      onClick: onClosePanel
    }];
  }, [inEditMode, onEditCustomer, onClosePanel, hasUnsavedChanges]);

  return (
    <BwjSidePanel
      showPanel={showPanel}
      title={'Customer Info'}
      panelType={PanelType.large}
      footerButtons={footerButtons}
      onDismiss={onClosePanel}
    >
      <BwjViewManager
        state={viewState}
        loadingState={{
          message: 'One moment please...'
        }}
        errorState={{
          message: 'An unexpected error occurred'
        }}
      >
        {inEditMode ?
        <CustomerEditor formModel={formModel} />
        :
        <CustomerViewer customer={customer} />}
      </BwjViewManager>
    </BwjSidePanel>
  );
};